import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Icon from '../components/Icon';

interface GuestGateCtx {
  /** Run `action` if signed in. If browsing as a guest, open the Login/Register
   *  prompt instead and return false (the action is blocked). */
  requireAuth: (action?: () => void) => boolean;
}

// Default (no provider in the tree — e.g. the logged-in MainRoutes): there is no
// guest gate, so "require auth" must simply RUN the action and allow it. The old
// default `() => true` silently dropped the action, which made every pay button
// wrapped in requireAuth() a no-op for logged-in users (the modal never opened).
const Ctx = createContext<GuestGateCtx>({
  requireAuth: (action?: () => void) => { action?.(); return true; },
});

export function GuestGateProvider({ children }: { children: ReactNode }) {
  const { isGuest } = useAuth();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const requireAuth = useCallback((action?: () => void) => {
    if (isGuest) { setOpen(true); return false; }
    action?.();
    return true;
  }, [isGuest]);

  const go = (path: string) => { setOpen(false); navigate(path); };

  return (
    <Ctx.Provider value={{ requireAuth }}>
      {children}
      <Modal open={open} onClose={() => setOpen(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 999, margin: '0 auto 12px',
            backgroundColor: colors.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="lock" size={22} color={colors.accentText} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: colors.text }}>{t('auth.guest.gateTitle')}</div>
          <div style={{ fontSize: 14, color: colors.textSub, marginTop: 6, lineHeight: 1.5 }}>{t('auth.guest.gateMessage')}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
          <Button label={t('auth.guest.register')} icon="user-plus" onClick={() => go('/register')} />
          <Button label={t('auth.guest.login')} icon="sign-in-alt" variant="outline" onClick={() => go('/login')} />
        </div>
      </Modal>
    </Ctx.Provider>
  );
}

export const useGuestGate = () => useContext(Ctx);
